import { getPrimaryColorPair } from '../../charts/shared/palettes';
import { J, JN, LEGEND_SPACING, TITLE_SPACING, fileHeader } from './shared';

export function genDivergingBar(chartType, config, t, data) {
  const sorted = [...data];
  if (config.barOrdering === 'ascending') sorted.sort((a, b) => a.value - b.value);
  else if (config.barOrdering === 'descending') sorted.sort((a, b) => b.value - a.value);

  const colorPair = getPrimaryColorPair(config, t);
  const posColor = colorPair.positive;
  const negColor = colorPair.negative;
  const centerVal = (config.centerLineValue !== '' && config.centerLineValue != null)
    ? parseFloat(config.centerLineValue)
    : 0;
  const centerStyle = config.centerLineStyle || 'solid';
  const barHeight = config.barHeight ?? 0.6;
  const showValues = !!config.showValues;
  const hasTitle = !!(config.title && String(config.title).trim());
  const showGrid = !!config.showGrid;
  const showLegend = !!config.showLegend;
  const legendPos = config.legendPosition || 'top';
  const decimals = config.valueDecimals ?? 1;

  const vals = sorted.map(d => d.value);
  const allVals = [...vals, centerVal];
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const pad = Math.max((maxV - minV) * 0.1, 1);
  const xMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : Math.min(minV - pad, centerVal);
  const xMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.max(maxV + pad, centerVal);

  const marginTop = (hasTitle ? 40 : 20) + (showLegend && legendPos === 'top' ? 22 : 0);
  const marginRight = (showLegend && legendPos === 'right' ? 150 : 0) + (showValues ? 60 : 30);
  const marginBottom = showLegend && legendPos === 'bottom' ? 80 : 70;
  const marginLeft = 110;

  const legendItems = [{ key: 'positive', label: 'Positive', color: posColor }, { key: 'negative', label: 'Negative', color: negColor }];

  const pathHelpers = `
function leftRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return 'M' + x + ',' + y + ' L' + (x + w) + ',' + y + ' L' + (x + w) + ',' + (y + h) + ' L' + x + ',' + (y + h) + ' Z';
  return 'M' + (x + w) + ',' + y + ' L' + (x + radius) + ',' + y + ' Q' + x + ',' + y + ' ' + x + ',' + (y + radius) + ' L' + x + ',' + (y + h - radius) + ' Q' + x + ',' + (y + h) + ' ' + (x + radius) + ',' + (y + h) + ' L' + (x + w) + ',' + (y + h) + ' Z';
}

function rightRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return 'M' + x + ',' + y + ' L' + (x + w) + ',' + y + ' L' + (x + w) + ',' + (y + h) + ' L' + x + ',' + (y + h) + ' Z';
  return 'M' + x + ',' + y + ' L' + (x + w - radius) + ',' + y + ' Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + radius) + ' L' + (x + w) + ',' + (y + h - radius) + ' Q' + (x + w) + ',' + (y + h) + ' ' + (x + w - radius) + ',' + (y + h) + ' L' + x + ',' + (y + h) + ' Z';
}`;

  const gridBlock = showGrid
    ? `          <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n`
    : '';
  const centerLineBlock = centerStyle !== 'none'
    ? `          <line x1={centerX} y1={0} x2={centerX} y2={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} strokeDasharray={${centerStyle === 'dashed' ? "'4 4'" : 'undefined'}} />\n`
    : '';
  const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: ${marginLeft}, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : '';
  const legendTopBlock = (showLegend && legendPos === 'top')
    ? `          <g transform="translate(0, -${LEGEND_SPACING.topOffset})">
            {LEGEND_ITEMS.map((item, i) => {
              const itemWidth = 95;
              const totalW = LEGEND_ITEMS.length * itemWidth;
              const startX = Math.max(0, (innerWidth - totalW) / 2);
              return (
              <g key={item.key} transform={\`translate(\${startX + i * itemWidth}, 0)\`}>
                <rect x={0} y={-${Math.round(LEGEND_SPACING.rectSize / 2)}} width={${LEGEND_SPACING.rectSize}} height={${LEGEND_SPACING.rectSize}} fill={item.color} />
                <text x={${LEGEND_SPACING.rectTextX}} y={1} fontSize={11} fill={THEME.legendTextColor}>{item.label}</text>
              </g>
            ); })}
          </g>
`
    : '';
  const legendBottomBlock = (showLegend && legendPos === 'bottom')
    ? `          <g transform={\`translate(0, \${innerHeight + ${LEGEND_SPACING.bottomGap}})\`}>
            {LEGEND_ITEMS.map((item, i) => {
              const itemWidth = 95;
              const totalW = LEGEND_ITEMS.length * itemWidth;
              const startX = Math.max(0, (innerWidth - totalW) / 2);
              return (
              <g key={item.key} transform={\`translate(\${startX + i * itemWidth}, 0)\`}>
                <rect x={0} y={-${Math.round(LEGEND_SPACING.rectSize / 2)}} width={${LEGEND_SPACING.rectSize}} height={${LEGEND_SPACING.rectSize}} fill={item.color} />
                <text x={${LEGEND_SPACING.rectTextX}} y={1} fontSize={11} fill={THEME.legendTextColor}>{item.label}</text>
              </g>
            ); })}
          </g>
`
    : '';
  const legendRightBlock = (showLegend && legendPos === 'right')
    ? `
      <g transform={\`translate(\${margin.left + innerWidth + ${LEGEND_SPACING.rightGap}}, \${margin.top + innerHeight / 2 - 12})\`}>
        {LEGEND_ITEMS.map((item, i) => (
          <g key={item.key} transform={\`translate(0, \${i * ${LEGEND_SPACING.rightItemHeight}})\`}>
            <rect x={0} y={-${Math.round(LEGEND_SPACING.rectSize / 2)}} width={${LEGEND_SPACING.rectSize}} height={${LEGEND_SPACING.rectSize}} fill={item.color} />
            <text x={${LEGEND_SPACING.rectTextX}} y={1} fontSize={11} fill={THEME.legendTextColor}>{item.label}</text>
          </g>
        ))}
      </g>
`
    : '';

  return (
    fileHeader(['@visx/scale', '@visx/group', '@visx/grid', '@visx/axis'], 'diverging-bar') +
    `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridColumns } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(sorted)};
const POS_COLOR = ${J(posColor)};
const NEG_COLOR = ${J(negColor)};
const CENTER_VAL = ${centerVal};
const BAR_HEIGHT_RATIO = ${barHeight};
const DECIMALS = ${decimals};
const THEME = ${JN(t)};
const X_DOMAIN = [${xMin}, ${xMax}];
const LEGEND_ITEMS = ${JN(legendItems)};
${pathHelpers}

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: ${marginRight}, bottom: ${marginBottom}, left: ${marginLeft} };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const yScale = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerHeight], padding: 0.35 }), [innerHeight]);
  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth], nice: true }), [innerWidth]);

  // Proportional bar width (see computeBarWidth in chartLayout.js)
  const _bwCap = innerHeight * Math.min(0.085, 0.7 / DATA.length);
  const bw = Math.max(2, Math.min(yScale.bandwidth() * (BAR_HEIGHT_RATIO / 0.6), _bwCap));
  const bOff = (yScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);
  const centerX = xScale(CENTER_VAL);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${legendTopBlock}${gridBlock}          {DATA.map((d, i) => {
            const val = d.value;
            const isPositive = val >= CENTER_VAL;
            const centerXPos = xScale(CENTER_VAL);
            const valueXPos = xScale(val);
            const barWidth = Math.abs(valueXPos - centerXPos);
            const y = (yScale(d.name) ?? 0) + bOff;
            if (barWidth < 0.5) return null;
            const barColor = isPositive ? POS_COLOR : NEG_COLOR;
            const pathD = isPositive
              ? rightRoundedRect(centerXPos, y, valueXPos - centerXPos, bw, effectiveRadius)
              : leftRoundedRect(valueXPos, y, centerXPos - valueXPos, bw, effectiveRadius);
            const fmt = (v) => v >= CENTER_VAL ? '+' + v.toFixed(DECIMALS) : v.toFixed(DECIMALS);
            return (
              <g key={d.name}>
                <path d={pathD} fill={barColor} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} />
                ${showValues ? `{true && <text x={isPositive ? valueXPos + 5 : valueXPos - 5} y={y + bw / 2} dominantBaseline="middle" textAnchor={isPositive ? 'start' : 'end'} fontSize={11} fontWeight={500} fill={THEME.foreground}>{fmt(val)}</text>}` : ''}
              </g>
            );
          })}
${centerLineBlock}          <AxisLeft scale={yScale} hideAxisLine hideTicks stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', dy: 10 })} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 10} textAnchor="middle" fontSize={14} fill={THEME.labelColor}>${config.xlabel || ''}</text>
${legendBottomBlock}        </Group>
${legendRightBlock}      </svg>
    </div>
  );
}
`
  );
}
